// ============================================================
// ClientDetailsSection.tsx
// Case-page Client Details section.
//
// Stage 2 redesign (case-page redesign, batch 1): renders the already-built
// ClientInfoSections view/edit cards (the same blocks as the client page,
// approved mockup casevo-client-details-view-edit.html, case context) over
// the case-row CLIENT SNAPSHOT:
//   person      = currentCase.clientDetails
//   onChange    = updatePerson → updateCase({ clientDetails }) (the case
//                 autosave path — flattenCase → PUT /api/cases/:id)
//   prefix      = 'clientDetails' (same fieldKeys → presence + live sync)
//   onBroadcast = broadcastClientFieldEdits (lookup / Cancel writes)
//   onFlush     = flushAutoSave (Save)
//   strict      = case rules (validateClientDetails ≡ validatePerson strict)
// plus the case-only "On this case" card (time off / days / medical
// attention, with the old one-write clearing rules), GP/Hospital cards gated
// on medical attention, the linked-client strip + "Update from client
// record" sync, the outdated banner + per-field outdated dots, and the
// shared section footer (Mark complete gated on validateClientDetails).
//
// ONE "Update this case" action (the old top-card "Update from client record"
// did exactly the same thing): when anything differs from the client record,
// an amber banner "N details differ from the client record" lists the fields
// (clientRecordDiff — every field the sync copies, blank-vs-filled included,
// format-only differences ignored) with one button; it opens a confirm
// listing each change "Field: case value → client record value" and applies
// on Confirm only. When nothing differs the strip says "Matches the client
// record ✓" and there's no button.
//
// Stage 2b (the Client Snapshot untangle) still holds: edits NEVER touch the
// live Client; propagation is the explicit sync-client action.
//
// (Batch 2) A CHILD (case DOB → isChildToday) or PROTECTED PARTY (the PI
// matter's flag): the client's own "Bills by email" card is replaced by one
// line pointing at the Litigation friend card (bills and statements go to
// the litigation friend; their consent is recorded there). A half-typed
// email / phone the autosave is holding back says so here ("Email not saved"
// — hooks/heldContact). Saves that matter to Settlement announce
// 'clientDetails' (utils/caseChangeEvents).
// ============================================================
import { flushAutoSave } from '../../hooks/useAutoSave'
import React, { useEffect, useMemo, useState } from 'react'
import { useCasevoStore } from '../../store/useCasevoStore'
import { ClientDetails } from '../../types/case.types'
import ModalFrame from '../shared/ModalFrame'
import { CLIENT_RECORD_FIELDS, clientRecordChanges, displayValue, type ClientRecordField } from './clientRecordDiff'
import { validateClientDetails } from '../../utils/validation'
import { useShouldShowOnboardingPrompt } from '../../hooks/useOnboardingPrompt'
import { broadcastClientFieldEdits } from '../../hooks/usePresence'
import { fetchCaseById, getLitigationFriendApi, syncClientApi, isClientRecordChanged } from '../../api/casevoApi'
import ActionBox from '../shared/ActionBox'

import ContactMismatchNote from '../shared/ContactMismatchNote'
import { contactGroupOf, type ContactGroup } from '../../utils/contactDiff'
import ClientInfoSections, { CARD, BTN_SEC, BTN_PRI, SM } from '../clientInfo/ClientInfoSections'
import BillEmailConsentCard from '../clientInfo/BillEmailConsentCard'
import MobileNumberCard, { mobileCardPropsOf } from '../clientInfo/MobileNumberCard'
import LitigationFriendCard, { LF_EDIT_ID, LF_ROOT_ID, dobIsoOf, isChildToday } from '../clientInfo/LitigationFriendCard'
import { heldContactMessage, useHeldContact } from '../../hooks/heldContact'
import { emitCaseChange, useCaseChange } from '../../utils/caseChangeEvents'
import { NOTE } from '../billing/billingUi'
import { caseDobOf } from '../../utils/caseDob'
import type { ClientInfoPerson, ClientInfoSectionKey, PersonField } from '../clientInfo/clientInfoRegistry'
import { useCaseSectionFrame, SectionFooter, KIcon } from '../case/sections/sectionKit'

// (Audit fix batch 2, Low 10) The server's capacity facts (litigation-friend GET).
export interface CapacityFacts { capacityToday: 'ADULT' | 'CHILD' | 'PROTECTED_PARTY' | 'UNKNOWN' | null; statementForChildOrProtected: boolean; lfRecorded: boolean }
// What the page shows: the server's answer once known ("can't tell" counts as
// not-an-adult: no adult consent card, the litigation friend's instead).
export const capacityShown = (local: 'ADULT' | 'CHILD' | 'PROTECTED_PARTY', f: CapacityFacts | null): 'ADULT' | 'CHILD' | 'PROTECTED_PARTY' | 'UNKNOWN' =>
  !f || !f.capacityToday ? local : f.capacityToday

// (Audit fix batch 2, Low 10) Re-read when the DOB, the protected-party flag
// or the litigation friend change (debounced); null until the server answers.
const useCapacityFacts = (caseId: string | null, dobText: string, protectedParty: boolean): CapacityFacts | null => {
  const [facts, setFacts] = useState<CapacityFacts | null>(null)
  const [rev, setRev] = useState(0)
  useCaseChange(caseId ?? '', ['litigationFriend', 'statement', 'settlementInputs'], () => setRev((n) => n + 1))
  const loaded = facts !== null
  useEffect(() => {
    if (!caseId) return undefined
    let live = true
    const t = window.setTimeout(() => {
      getLitigationFriendApi(caseId)
        .then((st) => { if (live) setFacts({ capacityToday: st.capacityToday ?? null, statementForChildOrProtected: !!st.statementForChildOrProtected, lfRecorded: !!st.litigationFriendName?.trim() }) })
        .catch(() => { if (live) setFacts(null) })
    }, loaded ? 600 : 0)
    return () => { live = false; window.clearTimeout(t) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, dobText, protectedParty, rev])
  return facts
}

// mockup .banner / .linked (casevo-client-details-view-edit.html :135-136, :189-193)
const AMBER_BANNER =
  'flex items-center gap-3 flex-wrap px-[14px] py-[11px] rounded-[12px] border text-[13.5px] mb-[14px] ' +
  'border-[#fcd34d] bg-[#fffbeb] text-[#92400e] dark:border-[rgba(180,83,9,0.5)] dark:bg-[rgba(120,53,15,0.28)] dark:text-[#fcd34d]'
const AMBER_BTN =
  `${BTN_SEC} ${SM} ml-auto !bg-transparent !border-[#fcd34d] !text-[#92400e] dark:!border-[rgba(180,83,9,0.5)] dark:!text-[#fcd34d]`

// Bring the Litigation friend card into view and focus its pencil.
export const focusLitigationFriendCard = (): void => {
  const root = document.getElementById(LF_ROOT_ID)
  root?.scrollIntoView?.({ block: 'start', behavior: 'smooth' })
  ;(document.getElementById(LF_EDIT_ID) ?? root)?.focus()
}

const ClientDetailsSection: React.FC = () => {
  const { currentCase, updateCase, setCurrentCaseById } = useCasevoStore()
  const frame = useCaseSectionFrame()
  const heldContact = useHeldContact()
  const details = currentCase?.clientDetails
  // (Audit fix batch 2, Low 10) The server's capacity rule (before any early return).
  const facts = useCapacityFacts(currentCase?.id ?? null, details?.dateOfBirth ?? '', currentCase?.protectedParty === true)
  const isEmpty =
    !!details && (
      !details.title && !details.forename && !details.surname
      && !details.addressLine1 && !details.addressLine2
      && !details.addressTown && !details.addressCounty && !details.addressPostcode
      && !details.dateOfBirth && !details.niNumber && details.hasNiNumber === null
      && !details.mainContactNumber && !details.emailAddress
      && !details.employmentStatus && !details.occupation
      && details.timeOffWork === null && details.daysOffWork === null
      && !details.descriptionOfInjuries && details.medicalAttention.length === 0
    )
  const showPrompt = useShouldShowOnboardingPrompt(isEmpty)
  // Stage 2c — sync-from-client state.
  const [isSyncing, setIsSyncing] = useState<boolean>(false)
  const [syncError, setSyncError] = useState<string | null>(null)
  const [syncJustSucceeded, setSyncJustSucceeded] = useState<boolean>(false)
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false)
  // Set when the confirm is re-shown because the client record changed
  // (the server refused the stale list — 409).
  const [confirmNotice, setConfirmNotice] = useState<string | null>(null)
  // ★ Bumped after every successful update: re-mounts the cards, so a card
  // that was open for editing closes instead of keeping its pre-update Cancel
  // snapshot (its Cancel would otherwise write the OLD values back).
  const [syncGeneration, setSyncGeneration] = useState(0)
  // (Client-sync) Contact groups (email / phone / address) edited on this case
  // and not saved yet: their note waits for Save (no note while typing).
  const [pendingContact, setPendingContact] = useState<Set<ContactGroup>>(() => new Set())
  // Auto-fade the success tick after 2s.
  useEffect(() => {
    if (!syncJustSucceeded) return
    const timer = setTimeout(() => setSyncJustSucceeded(false), 2000)
    return () => clearTimeout(timer)
  }, [syncJustSucceeded])
  // What "Update this case" would change: the case's copy
  // (currentCase.clientDetails) vs the live client record (currentCase.client)
  // over every field the sync copies (clientRecordDiff).
  const live = currentCase?.client ?? null
  const changes = useMemo(
    () => clientRecordChanges(details as unknown as Record<string, unknown> | undefined, live as unknown as Record<string, unknown> | null),
    [details, live],
  )
  const changeByField = useMemo(() => new Map(changes.map((c) => [c.field, c])), [changes])
  // (Client-sync batch) Email / phone / address differences get their own
  // note (ContactMismatchNote — bills use the client record); the "N details
  // differ" banner + "Update this case" cover everything else.
  const otherChanges = useMemo(() => changes.filter((c) => !contactGroupOf(c.field)), [changes])
  if (!currentCase || !details) return null
  const errors = validateClientDetails(currentCase)
  // Stage 2c — sync-from-client handlers. Click → confirm → flush pending
  // edits → POST /sync-client → refresh via setCurrentCaseById → "Updated".
  const handleOpenSyncConfirm = (): void => {
    if (!currentCase || isSyncing) return
    setSyncError(null)
    setConfirmNotice(null)
    setIsConfirmOpen(true)
  }
  const handleCancelSync = (): void => {
    setIsConfirmOpen(false)
    setConfirmNotice(null)
  }
  const handleConfirmSync = async (): Promise<void> => {
    if (!currentCase) return
    // ★ Exactly what the confirm listed: the record's own value for each
    // changed field, plus the record's version. The server copies only these,
    // and refuses (409) if the record moved on since this list was built.
    const record = (currentCase.client ?? {}) as unknown as Record<string, unknown>
    const confirmed: { fields: Record<string, unknown>; clientUpdatedAt?: string } = {
      fields: Object.fromEntries(otherChanges.map((c) => [c.field, record[c.field] ?? null])),
      ...(typeof record.updatedAt === 'string' ? { clientUpdatedAt: record.updatedAt } : {}),
    }
    setIsConfirmOpen(false)
    setConfirmNotice(null)
    setIsSyncing(true)
    setSyncError(null)
    try {
      await flushAutoSave() // save pending edits before the reload replaces them
      // (Two-click fix) Re-read first: the user's own earlier saves (e.g.
      // recording consent) move the record's version without changing these
      // values — send the fresh version. Values that DID change → the list again.
      let freshRecord: Record<string, unknown> | null = null
      try { freshRecord = ((await fetchCaseById(currentCase.id)) as unknown as { client?: Record<string, unknown> | null }).client ?? null } catch { /* the server still guards */ }
      if (freshRecord) {
        const rec = freshRecord
        if (otherChanges.some((c) => displayValue(c.field, rec[c.field]) !== c.recordValue)) {
          await setCurrentCaseById(currentCase.id)
          setConfirmNotice('The client record changed since you opened this. Here is the updated list — check it and confirm again.')
          setIsConfirmOpen(true)
          return
        }
        confirmed.fields = Object.fromEntries(otherChanges.map((c) => [c.field, rec[c.field] ?? null]))
        if (typeof rec.updatedAt === 'string') confirmed.clientUpdatedAt = rec.updatedAt
      }
      await syncClientApi(currentCase.id, confirmed)
      emitCaseChange(currentCase.id, 'clientDetails')
      await setCurrentCaseById(currentCase.id)
      setSyncGeneration((g) => g + 1) // close any open card (see syncGeneration)
      setSyncJustSucceeded(true)
    } catch (err) {
      if (isClientRecordChanged(err)) {
        // Reload the record and show the NEW list to confirm again.
        await setCurrentCaseById(currentCase.id)
        setConfirmNotice('The client record changed since you opened this. Here is the updated list — check it and confirm again.')
        setIsConfirmOpen(true)
        return
      }
      const message = err instanceof Error ? err.message : 'Failed to sync client details'
      setSyncError(message)
    } finally {
      setIsSyncing(false)
    }
  }
  // Per-field outdated props (dot + the record's value in the tooltip), handed
  // to every registry FormField via ctx.outdated — the same changes as the banner.
  const outdatedProps = (field: PersonField): { outdated: boolean; outdatedLiveValue?: string } | undefined => {
    if (!(CLIENT_RECORD_FIELDS as readonly string[]).includes(field)) return undefined
    const change = changeByField.get(field as ClientRecordField)
    return { outdated: !!change, outdatedLiveValue: change?.recordValue }
  }
  // Multi-key updater for the case-row snapshot — every card input writes
  // through here (ONE updateCase per change, so related keys never split;
  // e.g. hasNiNumber=false + niNumber='' or medicalAttention + its clears).
  // Spreads the LIVE store copy. `title` is a non-null string on the
  // snapshot (the old select wrote '' for "Select"), so null → ''.
  const updatePerson = (changes: Partial<ClientInfoPerson>) => {
    const liveDetails = useCasevoStore.getState().currentCase?.clientDetails ?? details
    const next = { ...changes } as Record<string, unknown>
    // (Client-sync batch) remember which contact groups this edit touched.
    const touched = Object.keys(next).map(contactGroupOf).filter((g): g is ContactGroup => !!g)
    if (touched.length) setPendingContact((prev) => new Set([...Array.from(prev), ...touched]))
    if ('title' in next && next.title === null) next.title = ''
    updateCase({ clientDetails: { ...liveDetails, ...(next as Partial<ClientDetails>) } })
  }
  // GP / Hospital cards only when medical attention includes them (as before).
  const sections: ClientInfoSectionKey[] = ['personal', 'contact', 'address', 'identity', 'onCase']
  if (details.medicalAttention.includes('gp')) sections.push('gp')
  if (details.medicalAttention.includes('hospital')) sections.push('hospital')

  // Save → persist, then the contact groups edited show their note (if the
  // case now differs from the client record). Cancel → the same, at once.
  const saveContact = async (): Promise<void> => {
    await flushAutoSave()
    setPendingContact(new Set())
  }
  // A protected party (the case's flag), or a child today (the case's DOB, else the record's).
  const localCapacity: 'ADULT' | 'CHILD' | 'PROTECTED_PARTY' = currentCase.protectedParty === true ? 'PROTECTED_PARTY'
    : isChildToday(dobIsoOf(caseDobOf(details.dateOfBirth, currentCase.client?.dateOfBirth ?? null).dob)) ? 'CHILD' : 'ADULT'
  // (Audit fix batch 2, Low 10) The SERVER's rule decides (who bills and
  // statements actually go to): re-read when the DOB / protected-party flag /
  // litigation friend change. Until it answers, the page's own reading.
  const capacity = capacityShown(localCapacity, facts)
  // The litigation friend's card: not an adult today (or can't tell), or the
  // current statement is a child's / protected party's (a child at settlement
  // who is 18 now — the statement still goes to the litigation friend).
  const lfApplies = capacity !== 'ADULT' || !!facts?.statementForChildOrProtected
  const first = details.forename.trim()
  const name = [details.title, details.forename, details.surname].map((x) => (x ?? '').trim()).filter(Boolean).join(' ')
  const topSlot = (
    <>
      {/* Linked-client strip (mockup .linked) + the Stage 2c sync action. */}
      <div className={`${CARD} flex items-center gap-3 flex-wrap px-[14px] py-3 mb-[14px]`}>
        <span aria-hidden="true" className="w-9 h-9 rounded-[11px] grid place-items-center text-white font-semibold bg-[linear-gradient(135deg,#1f4a36,#2d6a4f)] shrink-0">
          {(first || details.surname.trim() || '?').charAt(0).toUpperCase()}
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-[14px] text-casevo-forest">
            {name || 'Client'}
            {live && <span className="text-[12.5px] text-casevo-text-soft font-normal"> · Client #{live.clientNumber}</span>}
          </div>
          <div className="text-[12.5px] text-casevo-text-soft" aria-live="polite">
            {syncJustSucceeded
              ? <span className="text-casevo-brand dark:text-[#8fd6b0] inline-flex items-center gap-1"><KIcon name="check" size={12} strokeWidth={3} />Updated from the client record</span>
              : syncError
                ? <span className="text-casevo-danger">{syncError}</span>
                : live && changes.length === 0
                  ? <span className="text-casevo-brand dark:text-[#8fd6b0] inline-flex items-center gap-1">Matches the client record <KIcon name="check" size={12} strokeWidth={3} /></span>
                  : 'This case keeps its own copy of the client’s details.'}
          </div>
        </div>
      </div>
      {/* (Client-sync) The case's email / phone / address differs from the
          client record → THE one amber note (straight after Save, or any time
          later), each action one request; the page updates from its answer. */}
      {live && (
        <ContactMismatchNote className="mb-[14px]" caseId={currentCase.id}
          sides={{ caseCopy: details as unknown as Record<string, unknown>, record: live as unknown as Record<string, unknown>, clientUpdatedAt: typeof live.updatedAt === 'string' ? live.updatedAt : null }}
          exclude={Array.from(pendingContact)} before={flushAutoSave}
          onChanged={async (_g, direction, result) => {
            // A 409 → re-read the case so every field shows the current values
            // (the note keeps its "changed meanwhile" message — no remount).
            if (!result) { await setCurrentCaseById(currentCase.id); return }
            // The case copy changed → close any open card (its Cancel would
            // otherwise write the old values back — see syncGeneration).
            if (direction === 'toCase') setSyncGeneration((g) => g + 1)
          }} />
      )}
      {/* (M17) A half-typed email / phone isn't saved — never let it look saved. */}
      {heldContact.length > 0 && (
        <p role="status" className="m-0 mb-[14px] text-[13px] text-casevo-danger">
          <b className="font-semibold">{heldContactMessage(heldContact)}</b> — {heldContact.length > 1 ? 'they aren’t' : 'it isn’t'} valid yet. Finish typing it, or press Cancel to put the saved one back.
        </p>
      )}
      {/* Anything else differs from the client record → ONE banner + ONE action. */}
      {otherChanges.length > 0 && (
        <div role="status" className={AMBER_BANNER}>
          <KIcon name="alert" size={18} />
          <span className="flex-1 min-w-0">
            <b className="font-semibold">{otherChanges.length} detail{otherChanges.length === 1 ? '' : 's'} differ{otherChanges.length === 1 ? 's' : ''} from the client record</b>
            <span className="block text-[12.5px] mt-0.5 opacity-90">{otherChanges.map((c) => c.label).join(', ')}</span>
          </span>
          <button type="button" className={AMBER_BTN} onClick={handleOpenSyncConfirm} disabled={isSyncing}>{isSyncing ? 'Updating…' : 'Update this case'}</button>
        </div>
      )}
    </>
  )

  return (
    <>
      <ClientInfoSections
        key={`${currentCase.id}:${syncGeneration}`}
        person={details as unknown as ClientInfoPerson}
        onChange={updatePerson}
        fieldKeyPrefix="clientDetails"
        onBroadcast={broadcastClientFieldEdits}
        onFlush={saveContact}
        onCancel={() => setPendingContact(new Set())}
        strict
        sections={sections}
        title="Client details"
        subtitle={showPrompt ? 'Start by filling in the client’s basic information' : `${first ? `${first}’s` : 'The client’s'} details as recorded on this case`}
        saveHint="Saves to this case as you type · Cancel undoes"
        eyebrow={frame ? <>{frame.group}{frame.chip}</> : undefined}
        topSlot={topSlot}
        outdated={outdatedProps}
        protectedParty={currentCase.protectedParty === true}
        footer={<SectionFooter sectionKey="clientDetails" errors={errors} />}
      >
        {/* B4c (D4) The CLIENT RECORD's mobile for e-signature codes (the case keeps no copy — the card reads the record). Pilot only. */}
        {/* (B4c 1b) The case's client record carries the mobile (GET /api/cases/:id) — passed in, no second load. */}
        {/* (E-sign fixes B) + the record's main phone: a UK mobile there is used for codes (utils/esignMobile) — the same props as the Clients page. */}
        {currentCase.client?.id && <MobileNumberCard key={`mobile:${currentCase.client.id}`} {...mobileCardPropsOf(currentCase.client)} />}
      </ClientInfoSections>
      {/* B3 — consent to receive bills by email (held on the client record). */}
      {/* A child / protected party: bills + statements go to the litigation
          friend, whose consent is on their card — not the client's own. */}
      {/* (Low 10) Can't tell (no / unreadable DOB with a litigation friend): said, with the fix. */}
      {facts?.capacityToday === 'UNKNOWN' && (
        <ActionBox className="mt-4" title="We can’t tell who bills and statements go to"
          message="The date of birth is missing or can’t be read, and a litigation friend is recorded — so nothing can be emailed until the date of birth is filled in (or the client is confirmed an adult in Settlement)." />
      )}
      {currentCase.client?.id && capacity === 'ADULT' && (
        <BillEmailConsentCard clientId={currentCase.client.id} caseId={currentCase.id} clientName={details.forename} currentEmail={currentCase.client.emailAddress} currentNoEmail={currentCase.client.hasEmailAddress === false} />
      )}
      {lfApplies && capacity !== 'ADULT' && (
        <p data-testid="lf-consent-note" className={`${NOTE} m-0 mt-4 px-1`}>
          Bills and statements go to the litigation friend — their email consent is in{' '}
          <button type="button" className="p-0 bg-transparent border-0 underline text-casevo-brand hover:no-underline font-semibold rounded-sm focus:outline-none casevo-focus" onClick={focusLitigationFriendCard}>Litigation friend below</button>.
        </p>
      )}
      {/* (B4 follow-ups) Who acts for a child / protected party — the whole case's. */}
      {lfApplies && (
        <LitigationFriendCard caseId={currentCase.id} childOrProtected={capacity === 'PROTECTED_PARTY' || currentCase.protectedParty === true ? 'PROTECTED_PARTY' : 'CHILD'}
          clientAddress={{ line1: details.addressLine1 ?? '', line2: details.addressLine2 ?? '', town: details.addressTown ?? '', county: details.addressCounty ?? '', postcode: details.addressPostcode ?? '' }} />
      )}
      {/* Confirm: each change "Field: case value → client record value";
          applies on Confirm only. */}
      <ModalFrame
        isOpen={isConfirmOpen}
        onClose={handleCancelSync}
        size="md"
        portal
        eyebrow="Client details"
        eyebrowIcon={<KIcon name="sync" size={14} />}
        title="Update this case from the client record?"
        subtitle={`${otherChanges.length} detail${otherChanges.length === 1 ? '' : 's'} on this case will be replaced with the client record’s.`}
        footerRight={<>
          <button type="button" className={BTN_SEC} onClick={handleCancelSync}>Cancel</button>
          <button type="button" className={BTN_PRI} onClick={() => { void handleConfirmSync() }}>Update this case</button>
        </>}
      >
        {confirmNotice && <p role="alert" className="mb-3 text-[13px] text-[#92400e] dark:text-[#fcd34d]">{confirmNotice}</p>}
        {otherChanges.length === 0 && confirmNotice && <p className="text-[13.5px] text-casevo-text-soft">This case now matches the client record — nothing to update.</p>}
        <ul aria-label="Changes" className="m-0 p-0 list-none rounded-[12px] border border-casevo-border-soft divide-y divide-casevo-border-soft">
          {otherChanges.map((c) => (
            <li key={c.field} className="px-[14px] py-[10px] text-[13.5px] flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="font-semibold text-casevo-forest min-w-[150px]">{c.label}:</span>
              <span className="text-casevo-text-soft line-through decoration-casevo-text-soft/50">{c.caseValue}</span>
              <span aria-hidden="true" className="text-casevo-text-soft">→</span>
              <span className="sr-only">changes to</span>
              <span className="text-casevo-forest font-medium">{c.recordValue}</span>
            </li>
          ))}
        </ul>
      </ModalFrame>
    </>
  )
}
export default ClientDetailsSection
